import React, { useState } from 'react';
import './MarketplacePracticeLocation.scss';
import { Input } from '@availity/form';
import { FormGroup } from 'reactstrap';
import { EntityLocationInfo } from '../../../../slices/profiles-data-slice/models/entity-location-info';
import {
    LocationType,
    LocationTypeOptions,
} from '../../../../slices/marketplace/timeline/models/enums/location-type.enum';
import Helpers from '../../../../utils/helper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import ConfirmationModal from '../../../Modal/ConfirmationModal';
import StateSelectField from '../../../FormFields/StateSelectField/StateSelectField';
import { getIn, useFormikContext } from 'formik';
import { useServiceFocuses } from '../../../../custom-hooks/useServiceFocuses';
import { isOtherFocus } from '../../../../containers/UserPages/Login/Modals/PracticeRegistrationForm/utils';
import { NotificationFlag } from '../../../NotificationFlag/NotificationFlag';
import { NotificationFlagsCategories } from '../../../../models/NotificationFlags/NotificationFlagsCategories';

interface MarketplacePracticeLocationProps {
    location: EntityLocationInfo;
    index: number;
    fieldPrefix: string;
    setFormFieldValue(name: string, value: any): void;
    removeLocation(locationIndex: number): void;
    isEditable?: boolean;
    isOtherPracticeFocusColumnVisible?: boolean;
    timelineId?: number;
    practiceId?: string;
}

const MarketplacePracticeLocation = (
    props: MarketplacePracticeLocationProps
) => {
    const { isEditable = true, timelineId, practiceId } = props;
    const serviceFocuses = useServiceFocuses();
    const location = props.location;
    const [
        isDeleteConfirmationPopupVisible,
        setIsDeleteConfirmationPopupVisible,
    ] = useState(false);
    const { values } = useFormikContext();

    const isOtherPracticeFocusInputVisible = (fieldPath: string) => {
        const selectedFocus = getIn(values, fieldPath);
        return isOtherFocus(serviceFocuses, selectedFocus);
    };
    const handleLocationRemoval = () => {
        props.removeLocation(props.index);
    };

    const locationFieldBadge = (field: string) =>
        timelineId && practiceId ? (
            <NotificationFlag
                flagClassName="notification-flag-wrapper"
                doNotRemove={true}
                eventCode={`${NotificationFlagsCategories.MpCode}.${timelineId}.${NotificationFlagsCategories.SellerProfileUpdatedCode}.PracticeProfiles.${practiceId}.PracticeLocations.${location.id}.${field}`}
            />
        ) : null;

    const locationRowBadge = () =>
        timelineId && practiceId ? (
            <NotificationFlag
                flagClassName="notification-flag-wrapper"
                doNotRemove={true}
                includePrefix={true}
                eventCode={`${NotificationFlagsCategories.MpCode}.${timelineId}.${NotificationFlagsCategories.SellerProfileUpdatedCode}.PracticeProfiles.${practiceId}.PracticeLocations.${location.id}`}
            />
        ) : null;

    return (
        <>
            <tr className="practice-location">
                <td>
                    <div className="cell-with-badge">
                        <FormGroup>
                            <Input
                                name={`${props.fieldPrefix}[${props.index}].dbaName`}
                                type="text"
                                disabled={!isEditable}
                                className="form-control"
                            />
                        </FormGroup>
                        {locationFieldBadge('DbaName')}
                        {locationRowBadge()}
                    </div>
                </td>
                <td>
                    <div className="cell-with-badge">
                        <FormGroup>
                            <Input
                                name={`${props.fieldPrefix}[${props.index}].addressInfo.streetAddress`}
                                type="text"
                                className="form-control"
                                disabled={!isEditable}
                            />
                        </FormGroup>
                        {locationFieldBadge('AddressInfo.StreetAddress')}
                    </div>
                </td>
                <td>
                    <div className="cell-with-badge">
                        <FormGroup>
                            <Input
                                name={`${props.fieldPrefix}[${props.index}].addressInfo.city`}
                                type="text"
                                className="form-control"
                                disabled={!isEditable}
                            />
                        </FormGroup>
                        {locationFieldBadge('AddressInfo.City')}
                    </div>
                </td>
                <td>
                    <div className="cell-with-badge">
                        <StateSelectField
                            name={`${props.fieldPrefix}[${props.index}].addressInfo.state`}
                            disabled={!isEditable}
                            className=""
                            label=""
                            placeholder="Select State"
                        />
                        {locationFieldBadge('AddressInfo.State')}
                    </div>
                </td>
                <td>
                    <div className="cell-with-badge">
                        <FormGroup>
                            <Input
                                name={`${props.fieldPrefix}[${props.index}].addressInfo.zipCode`}
                                type="text"
                                className="form-control"
                                disabled={!isEditable}
                                maxLength={5}
                                minLength={5}
                                pattern="[0-9]{5}"
                            />
                        </FormGroup>
                        {locationFieldBadge('AddressInfo.ZipCode')}
                    </div>
                </td>
                <td>
                    <div className="cell-with-badge">
                        <FormGroup>
                            <Input
                                name={`${props.fieldPrefix}[${props.index}].locationType`}
                                type="select"
                                disabled={!isEditable}
                                className="form-control"
                                defaultValue={LocationType.Urban}>
                                {Object.entries(LocationTypeOptions).map(
                                    ([key, value]) => (
                                        <option key={key} value={key}>
                                            {value}
                                        </option>
                                    )
                                )}
                            </Input>
                        </FormGroup>
                        {locationFieldBadge('LocationType')}
                    </div>
                </td>
                <td>
                    <div className="cell-with-badge">
                        <FormGroup>
                            <Input
                                name={`${props.fieldPrefix}[${props.index}].fteDvms`}
                                type="select"
                                className="form-control"
                                disabled={!isEditable}
                                defaultValue={0.5}
                                onChange={(event) => {
                                    props.setFormFieldValue(
                                        `${props.fieldPrefix}[${props.index}].fteDvms`,
                                        event.target.value
                                    );
                                }}>
                                {Helpers.getFteDvmsNumberList().map((option) => (
                                    <option key={option.id} value={option.id}>
                                        {option.value}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                        {locationFieldBadge('FteDvms')}
                    </div>
                </td>
                <td className="practice-focus">
                    <div className="cell-with-badge">
                        <FormGroup>
                            <Input
                                name={`${props.fieldPrefix}[${props.index}].practiceFocusType`}
                                type="select"
                                className="form-control"
                                disabled={!isEditable}>
                                {serviceFocuses.map((option) => (
                                    <option key={option.id} value={option.id}>
                                        {option.name}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                        {locationFieldBadge('PracticeFocusType')}
                    </div>
                </td>
                <td hidden={!props.isOtherPracticeFocusColumnVisible}>
                    <FormGroup>
                        <Input
                            hidden={
                                !isOtherPracticeFocusInputVisible(
                                    `${props.fieldPrefix}[${props.index}].practiceFocusType`
                                )
                            }
                            name={`${props.fieldPrefix}[${props.index}].otherPracticeType`}
                            type="text"
                            className="form-control"
                            disabled={!isEditable}
                        />
                    </FormGroup>
                </td>
                <td className="location-number">
                    <FontAwesomeIcon
                        size="lg"
                        icon={faTrash}
                        className={
                            isEditable ? 'delete-icon' : 'delete-icon disabled'
                        }
                        onClick={() =>
                            setIsDeleteConfirmationPopupVisible(true)
                        }
                    />
                </td>
            </tr>
            {isDeleteConfirmationPopupVisible && (
                <ConfirmationModal
                    isVisible={true}
                    setIsVisible={setIsDeleteConfirmationPopupVisible}
                    header={'Location Removal'}
                    confirmationText={`Are you sure you want to remove ${location.locationName}?`}
                    onConfirm={handleLocationRemoval}
                    nextButtonText={'Remove'}
                    cancelButtonText={'Cancel'}
                    params={null}
                />
            )}
        </>
    );
};
export default MarketplacePracticeLocation;
